import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Col,
    Container,
    Dropdown,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
    Placeholder,
} from "react-bootstrap";
import TicketsSearch from "../tickets/TicketsSearch";
import EmbedListQuickFilters, { REACHABLE_QUICK_FILTER_OPTIONS } from "./embedList/EmbedListQuickFilters";
import EmbedListActiveQuickFilter, {
    getEmbedListActiveFilterState,
} from "./embedList/EmbedListActiveQuickFilter";
import EmbedListLayout from "./embedList/EmbedListLayout";
import EmbedListFilter, { EMBED_DOMAINS_ADVANCED_FILTER_OPTIONS } from "./embedList/EmbedListFilter";
import EmbedListSortableTh from "./embedList/EmbedListSortableTh";
import {
    buildEmbedListApiParams,
    patchEmbedListRowActive,
} from "./embedList/embedListUtils";
import axios from "axios";
import Cookies from "js-cookie";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import plus from "../../../../css/img/plus.svg";
import { baseURL } from "../../../helpers/baseURL";
import MainContainer from "../../MainContainer";
import tableStyles from "../tickets/TicketsTable.module.css";
import { useEmbedFormDialogs } from "./useEmbedFormDialogs";
import "./embed-form-editor.css";
import { EmbedFormBtn } from "./embedFormButtons";
import { EmbedFormsActiveSelect } from "./embedFormsActiveSelect";
import { EMBED_FORMS_ACTIONS_MENU_POPPER } from "./embedFormsActionsMenu";
import {
    normalizeTrustedDomainInput,
    trustedDomainHref,
} from "./embedTrustedDomainUtils";
import { handleEmbedAuthRedirect } from "./embedAuthRedirect";

const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get("access_token")}`,
});

/** Warning copy when delete/deactivate would block live embed widgets. */
function trustedDomainImpactMessage(row, action) {
    const count = Number(row?.embed_forms_count) || 0;
    if (count === 0) {
        return action === "delete"
            ? "You will not be able to recover it."
            : "";
    }

    const names = Array.isArray(row?.embed_form_names)
        ? row.embed_form_names.filter(Boolean)
        : [];
    const shown = names.slice(0, 5);
    const extra = names.length - shown.length;
    const namePart =
        shown.length > 0
            ? `: ${shown.join(", ")}${extra > 0 ? ` and ${extra} more` : ""}`
            : "";

    const formWord = count === 1 ? "form" : "forms";
    const blockNote =
        action === "delete"
            ? "Deleting it will stop those widgets on customer sites (403) until each form is linked to another active trusted domain."
            : "Deactivating it will stop those widgets on customer sites until you activate this domain again or update each form's domains.";

    if (action === "delete") {
        return `${count} active embed ${formWord} use this domain${namePart}. ${blockNote} You will not be able to recover this domain entry.`;
    }

    return `${count} active embed ${formWord} use this domain${namePart}. ${blockNote}`;
}

const DEFAULT_DOMAIN_COLUMNS = {
    domain: { visible: true, label: "Domain" },
    created_at: { visible: true, label: "Created" },
    is_reachable: { visible: true, label: "Reachable" },
    is_active: { visible: true, label: "Active" },
    embed_forms_count: { visible: true, label: "Used by forms" },
};

const DEFAULT_DOMAIN_FILTERS = {
    text: "",
    is_active: [],
    is_reachable: [],
    created_at: "",
    created_at_range: [],
};

const EmbedTrustedDomains = () => {
    const { confirmDialog, alertDialog, dialogNodes } = useEmbedFormDialogs();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(DEFAULT_DOMAIN_FILTERS);
    const [sort, setSort] = useState({ by: "domain", direction: "asc" });
    const [columns, setColumns] = useState(() => {
        try {
            const saved = localStorage.getItem("embedTrustedDomainsTableColumns");
            return saved ? JSON.parse(saved) : DEFAULT_DOMAIN_COLUMNS;
        } catch {
            return DEFAULT_DOMAIN_COLUMNS;
        }
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [domainDraft, setDomainDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [recheckingId, setRecheckingId] = useState(null);
    const [updatingActiveId, setUpdatingActiveId] = useState(null);
    const [error, setError] = useState("");

    const load = async (silent = false, filtersSnapshot = filters, sortSnapshot = sort) => {
        if (!silent) {
            setLoading(true);
        }
        try {
            const params = buildEmbedListApiParams(filtersSnapshot, sortSnapshot, null);
            const { data } = await axios.get(
                `${baseURL}ticket-embed-trusted-domains`,
                { headers: authHeaders(), params }
            );
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            if (!silent) {
                setRows([]);
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        load(false, filters, sort);
    }, [filters, sort]);

    const handleSearchDebounced = useCallback((text) => {
        setFilters((prev) => ({ ...prev, text: (text ?? "").trim() }));
    }, []);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleSortChange = (column) => {
        if (column === "is_active") {
            setSort({
                by: column,
                direction:
                    sort.by === column && sort.direction === "desc" ? "asc" : "desc",
            });
            return;
        }
        setSort({
            by: column,
            direction: sort.by === column && sort.direction === "asc" ? "desc" : "asc",
        });
    };

    const handleColumnVisibilityChange = (column) => {
        const updated = {
            ...columns,
            [column]: { ...columns[column], visible: !columns[column].visible },
        };
        setColumns(updated);
        localStorage.setItem("embedTrustedDomainsTableColumns", JSON.stringify(updated));
    };

    const domainsQuickFilters = useMemo(
        () => [{ key: "is_reachable", label: "Reachable", options: REACHABLE_QUICK_FILTER_OPTIONS }],
        []
    );

    const handleActiveQuickFilterChange = (isActiveValue) => {
        setFilters((prev) => ({ ...prev, is_active: isActiveValue }));
    };

    const visibleColumnCount =
        Object.values(columns).filter((c) => c.visible).length + 1;

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
        setDomainDraft("");
        setError("");
    };

    const openAdd = () => {
        setError("");
        setEditingId(null);
        setDomainDraft("");
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setError("");
        setEditingId(row.id);
        setDomainDraft(normalizeTrustedDomainInput(row.domain || ""));
        setModalOpen(true);
    };

    const save = async (ev) => {
        ev.preventDefault();
        const domain = normalizeTrustedDomainInput(domainDraft);
        if (!domain) {
            setError("Enter a domain hostname.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            if (editingId) {
                await axios.post(
                    `${baseURL}ticket-embed-trusted-domain-update/${editingId}`,
                    { domain },
                    { headers: authHeaders() }
                );
            } else {
                await axios.post(
                    `${baseURL}ticket-embed-trusted-domain`,
                    { domain },
                    { headers: authHeaders() }
                );
            }
            closeModal();
            load(true);
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            setError(
                e.response?.data?.message ||
                    "Could not save domain. It must be valid and reachable."
            );
        } finally {
            setSaving(false);
        }
    };

    const setActive = async (row, isActive) => {
        const id = row.id;
        const applyActive = async () => {
            setUpdatingActiveId(id);
            try {
                await axios.post(
                    `${baseURL}ticket-embed-trusted-domain-toggle/${id}`,
                    { is_active: isActive },
                    { headers: authHeaders() }
                );
                const activeFilter = getEmbedListActiveFilterState(
                    filters.is_active
                );
                setRows((prev) =>
                    patchEmbedListRowActive(prev, id, isActive, activeFilter)
                );
            } catch (e) {
                if (handleEmbedAuthRedirect(e)) {
                    return;
                }
                alertDialog({
                    title: "Could not update status",
                    message:
                        "The domain status could not be updated. Please try again.",
                });
            } finally {
                setUpdatingActiveId(null);
            }
        };

        if (!isActive) {
            const impact = trustedDomainImpactMessage(row, "deactivate");
            if (impact) {
                confirmDialog({
                    title: "Deactivate trusted domain?",
                    message: impact,
                    confirmLabel: "Deactivate",
                    cancelLabel: "Cancel",
                    onConfirm: applyActive,
                });
                return;
            }
        }

        await applyActive();
    };

    const recheck = async (id) => {
        setRecheckingId(id);
        try {
            await axios.post(
                `${baseURL}ticket-embed-trusted-domain-recheck/${id}`,
                {},
                { headers: authHeaders() }
            );
            load(true);
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            alertDialog({
                title: "Domain not reachable",
                message:
                    e.response?.data?.message || "Domain is not reachable.",
            });
            load(true);
        } finally {
            setRecheckingId(null);
        }
    };

    const remove = (row) => {
        confirmDialog({
            title: "Delete trusted domain?",
            message: trustedDomainImpactMessage(row, "delete"),
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            onConfirm: async () => {
                try {
                    await axios.post(
                        `${baseURL}ticket-embed-trusted-domain-delete/${row.id}`,
                        {},
                        { headers: authHeaders() }
                    );
                    load(true);
                } catch (e) {
                    if (handleEmbedAuthRedirect(e)) {
                        return;
                    }
                    alertDialog({
                        title: "Could not delete",
                        message:
                            "The trusted domain could not be deleted. Please try again.",
                    });
                }
            },
        });
    };

    const resetActionsMenuReady = (dropdownToggleId) => {
        const el = document.getElementById(dropdownToggleId);
        if (!el) {
            return;
        }
        if (el.__embedMenuRevealRafId != null) {
            cancelAnimationFrame(el.__embedMenuRevealRafId);
            el.__embedMenuRevealRafId = null;
        }
        el.parentElement
            ?.querySelector(".dropdown-menu")
            ?.removeAttribute("data-embed-menu-ready");
    };

    const renderLoadingSkeleton = () => {
        const skeletonRows = 8;
        return (
            <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                <Table className={tableStyles.table}>
                    <thead>
                        <tr>
                            <th>Domain</th>
                            <th>Created</th>
                            <th>Reachable</th>
                            <th>Active</th>
                            <th>Used by forms</th>
                            <th
                                className={tableStyles.stickyActions}
                                style={{ width: 110, textAlign: "center" }}
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: skeletonRows }).map((_, idx) => (
                            <tr key={idx}>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={7}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td className="text-muted">
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={6}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td className="embed-forms-reachable-cell">
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            style={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: 999,
                                                display: "inline-block",
                                            }}
                                        />
                                    </Placeholder>
                                </td>
                                <td className={tableStyles.selectCell}>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={8}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={3}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td
                                    className={tableStyles.stickyActions}
                                    style={{ textAlign: "center" }}
                                >
                                    <div style={{ display: "inline-flex", gap: 5 }}>
                                        <Placeholder.Button
                                            variant="secondary"
                                            style={{
                                                width: 26,
                                                height: 26,
                                                padding: 0,
                                                borderRadius: 5,
                                            }}
                                            aria-hidden="true"
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <MainContainer>
            <Container fluid className="embed-forms-page">
                <Row className="pt-4 pb-2">
                    <Col className="table-page-header">
                        <h5 className="flex-grow-1 m-0 text-align-left">Trusted domains</h5>
                    </Col>
                </Row>
                <Row className="pb-3">
                    <Col className="d-flex flex-wrap align-items-start gap-2">
                        <TicketsSearch
                            value={filters.text}
                            onDebouncedChange={handleSearchDebounced}
                            isLoading={loading}
                            placeholder="Search domains"
                        />
                        <EmbedListQuickFilters
                            quickFilters={domainsQuickFilters}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            isLoading={loading}
                            extraClearKeys={["is_active"]}
                            isExtraFilterActive={(key, f) =>
                                key === "is_active" &&
                                getEmbedListActiveFilterState(f.is_active) !== "all"
                            }
                            afterQuickFilters={
                                <EmbedListActiveQuickFilter
                                    value={filters.is_active}
                                    onChange={handleActiveQuickFilterChange}
                                    isLoading={loading}
                                    itemLabel="domains"
                                />
                            }
                        />
                        <div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
                            <EmbedListLayout
                                columns={columns}
                                onColumnVisibilityChange={handleColumnVisibilityChange}
                            />
                            <EmbedListFilter
                                filterOptions={EMBED_DOMAINS_ADVANCED_FILTER_OPTIONS}
                                isLoading={loading}
                                filters={filters}
                                onFilterChange={handleFilterChange}
                            />
                            <Button
                                type="button"
                                variant={EmbedFormBtn.primary}
                                onClick={openAdd}
                                style={{ whiteSpace: "nowrap" }}
                                disabled={loading}
                            >
                                <img src={plus} alt="" />{" "}
                                <span className="ms-1">Add domain</span>
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col className="overflow-x-auto">
                        {loading ? (
                            renderLoadingSkeleton()
                        ) : (
                            <div
                                className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}
                            >
                                <Table className={tableStyles.table} hover>
                                    <thead>
                                        <tr>
                                            {columns.domain.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="domain"
                                                    label={columns.domain.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            {columns.created_at.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="created_at"
                                                    label={columns.created_at.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            {columns.is_reachable.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="is_reachable"
                                                    label={columns.is_reachable.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            {columns.is_active.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="is_active"
                                                    label={columns.is_active.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            {columns.embed_forms_count.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="embed_forms_count"
                                                    label={columns.embed_forms_count.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            <th
                                                className={tableStyles.stickyActions}
                                                style={{
                                                    width: 110,
                                                    textAlign: "center",
                                                }}
                                            >
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={visibleColumnCount}
                                                    className="text-muted embed-ticket-forms-table__empty"
                                                >
                                                    No trusted domains yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            rows.map((r) => (
                                                <tr key={r.id}>
                                                    {columns.domain.visible && (
                                                    <td className="embed-trusted-domains-domain-cell">
                                                        <a
                                                            href={trustedDomainHref(
                                                                r.domain
                                                            )}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="link embed-trusted-domains-domain-link"
                                                        >
                                                            {r.domain}
                                                        </a>
                                                    </td>
                                                    )}
                                                    {columns.created_at.visible && (
                                                    <td className="text-muted">
                                                        {r.created_at || "—"}
                                                    </td>
                                                    )}
                                                    {columns.is_reachable.visible && (
                                                    <td className="embed-forms-reachable-cell">
                                                        {recheckingId === r.id ? (
                                                            <Spinner
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-label="Checking reachability"
                                                            />
                                                        ) : r.is_reachable ? (
                                                            <FaCheckCircle
                                                                className="text-success"
                                                                title="Reachable"
                                                                aria-label="Reachable"
                                                            />
                                                        ) : (
                                                            <FaExclamationCircle
                                                                className="text-danger"
                                                                title="Not reachable"
                                                                aria-label="Not reachable"
                                                            />
                                                        )}
                                                    </td>
                                                    )}
                                                    {columns.is_active.visible && (
                                                    <td
                                                        className={
                                                            tableStyles.selectCell
                                                        }
                                                    >
                                                        <EmbedFormsActiveSelect
                                                            isActive={r.is_active}
                                                            disabled={
                                                                updatingActiveId ===
                                                                r.id
                                                            }
                                                            onChange={(nextActive) =>
                                                                setActive(
                                                                    r,
                                                                    nextActive
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    )}
                                                    {columns.embed_forms_count.visible && (
                                                    <td>
                                                        {r.embed_forms_count ??
                                                            0}
                                                    </td>
                                                    )}
                                                    <td
                                                        className={
                                                            tableStyles.stickyActions
                                                        }
                                                        style={{
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        <Dropdown
                                                            align="end"
                                                            onToggle={(
                                                                nextShow
                                                            ) => {
                                                                if (!nextShow) {
                                                                    resetActionsMenuReady(
                                                                        `embed-trusted-dd-${r.id}`
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Dropdown.Toggle
                                                                as="button"
                                                                type="button"
                                                                className={
                                                                    tableStyles.actionBtn
                                                                }
                                                                id={`embed-trusted-dd-${r.id}`}
                                                                disabled={
                                                                    recheckingId ===
                                                                    r.id
                                                                }
                                                            >
                                                                <BsThreeDotsVertical
                                                                    size={11}
                                                                />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu
                                                                className="embed-ticket-forms-actions-menu"
                                                                popperConfig={
                                                                    EMBED_FORMS_ACTIONS_MENU_POPPER
                                                                }
                                                            >
                                                                <Dropdown.Item
                                                                    disabled={
                                                                        recheckingId ===
                                                                        r.id
                                                                    }
                                                                    onClick={() =>
                                                                        recheck(
                                                                            r.id
                                                                        )
                                                                    }
                                                                >
                                                                    {recheckingId ===
                                                                    r.id ? (
                                                                        <span className="d-inline-flex align-items-center gap-2">
                                                                            <Spinner
                                                                                animation="border"
                                                                                size="sm"
                                                                                role="status"
                                                                                aria-hidden="true"
                                                                            />
                                                                            Checking…
                                                                        </span>
                                                                    ) : (
                                                                        "Re-check reachability"
                                                                    )}
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    onClick={() =>
                                                                        openEdit(
                                                                            r
                                                                        )
                                                                    }
                                                                >
                                                                    Edit
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    className="text-danger"
                                                                    onClick={() =>
                                                                        remove(
                                                                            r
                                                                        )
                                                                    }
                                                                >
                                                                    Delete
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>

            {dialogNodes}
            <Modal
                show={modalOpen}
                onHide={closeModal}
                centered
                className="embed-forms-ui"
                enforceFocus={false}
                restoreFocus={false}
                autoFocus={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingId ? "Edit domain" : "Add trusted domain"}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={save}>
                    <Modal.Body>
                        {error ? (
                            <div className="alert alert-danger py-2 small">
                                {error}
                            </div>
                        ) : null}
                        <Form.Group>
                            <Form.Label>Hostname</Form.Label>
                            <Form.Control
                                id="embed-trusted-domain-hostname"
                                value={domainDraft}
                                onChange={(e) =>
                                    setDomainDraft(
                                        normalizeTrustedDomainInput(
                                            e.target.value
                                        )
                                    )
                                }
                                placeholder="example.com"
                                required
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                data-lpignore="true"
                                autoFocus
                            />
                            <Form.Text>
                                No protocol or path. We verify DNS and that the
                                site responds before saving.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            type="button"
                            variant={EmbedFormBtn.danger}
                            onClick={closeModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant={EmbedFormBtn.primary}
                            disabled={saving}
                        >
                            {saving ? "Checking…" : "Save"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </MainContainer>
    );
};

export default EmbedTrustedDomains;
